import type { ReactElement } from "react";

import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import type { CRMListObjectsType, CrmFeedLevel, IntegrationSyncStatus } from "../types";

export enum CreateSuppressionListStep {
    UPLOAD_FILE = 0,
    ASSIGN_NAME = 1,
}

export type ErrorTexts = Partial<{ [key in keyof CompanyParsingErrors | keyof ContactsParsingErrors]: string }>;

export type SupportTextProps = {
    hasError: boolean;
    parsingErrors: ParsingErrors;
    customHint?: string | ReactElement;
    errorTexts?: ErrorTexts;
    width?: number | string;
    hasCustomTracking?: boolean;
    type: EnrichmentType;
};

export type ParseDomainsResult = {
    parsedDomains: string[];
    totalRowCount: number;
    allDomainValues: Record<string, string[]>;
    headerNames: string[];
    domainColumn: string;
};

export type ParsedContactsResult = {
    fileParsed: Record<string, string[]>;
    linkedinHeaderNames: string[];
    fullnameHeaderNames: string[];
    companyHeaderNames: string[];
    headerNames: string[];
    totalRowCount: number;
};

export type ContactsHeaderSelection = Partial<{
    linkedinUrl: string;
    fullName: string;
    domain: string;
    firstName: string;
    lastName: string;
}>;

export type SuppressionList = {
    id: number;
    name: string;
    created: string;
    domainsCount: number;
    ownerFirstName: string;
    ownerLastName: string;
    isOwnedByRequestedUser: boolean;
};

export type UploadSuppressionListPayload = { listName: string; domains: string[] };

export type CompanyParsingErrors = {
    isOverTheLimit: boolean;
    notADomains: boolean;
    columnsNumber: boolean;
    format: boolean;
};

export type ContactsParsingErrors = {
    isOverTheLimit: boolean;
    format: boolean;
    noRequiredFields: boolean;
    quotaExceeded: boolean;
    emptyColumns: boolean;
};

export type ParsingErrors = CompanyParsingErrors | ContactsParsingErrors;

export type IntegrationStatus = {
    ownerFirstName: string;
    ownerLastName: string;
    domainsCount: number;
    syncStatus: IntegrationSyncStatus;
    lastSynced: string;
    isExportEnabled: boolean;
    isContactExportEnabled: boolean;
    isLeadsExportEnabled: boolean;
    isCompanyAsLeadExportEnabled: boolean;
    isLeadsFilterEnabled: boolean;
    isCrmListEnabled: boolean;
    isCrmLabelsEnabled: boolean;
    isCrmFeedEnabled: boolean;
    crmFeedLevel: CrmFeedLevel;
    syncFailedReasonCode: number;
    crmListObjectsType: CRMListObjectsType;
};

export type CrmStateInfo = {
    accountExportFieldsCount: number;
    contactExportFieldsCount: number;
    leadExportFieldsCount: number;
};

export type CrmExportLimit = {
    accountsLimit: number;
    contactsLimit: number;
    leadsLimit: number;
};

export type IntegrationStatusResponse = {
    crmConnectionStatus: IntegrationStatus;
    crmStateInfo: CrmStateInfo;
    crmExportLimit?: CrmExportLimit;
    isCurrentUserVerifiedByCrm: boolean;
    companyAsLeadExportClaimEnabled: boolean;
    predefinedOnCreateUpdateClaimEnabled: boolean;
};

export type FetchDomainsParams = {
    countries: readonly number[];
    domainsCount: number;
    leadsType: LeadsType;
    listParams?: { id: string };
};

export type FetchDomainsThunk<T extends FetchDomainsParams> = (
    params: T,
) => (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => Promise<string[]>;

export type FetchDomainsFn<Params extends FetchDomainsParams> = (
    params: Params,
) => Promise<readonly string[] | undefined>;

export type WebsitesIntegrationExportItemProps = {
    isDisabled: boolean;
    onClick: VoidFunction;
    tooltipDisabledContent?: string;
    tooltipContent?: string;
    isUseLockedIcon?: boolean;
    text?: string;
};
